import { useLocation } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import {
  NAV_SECTIONS,
  type NavChild,
  type NavGroup,
  type NavSection,
  type NavVisibilityContext,
} from '@/config/navigation';
import type { PermissionCode } from '@/constants/permissions';
import { profileQueryOptions } from '@/features/auth/api/auth.api';
import { usePermissions } from '@/features/auth/hooks/usePermissions';
import type { PcmpPermission } from '@/features/pcmp/api/pcmpAccess.api';
import { usePcmpPermissions } from '@/features/pcmp/hooks/usePcmpPermissions';
import { USER_TYPE } from '@/features/users/constants/user.constants';
import { useAuthStore } from '@/store/auth.store';

type CanPcmp = (...permissions: PcmpPermission[]) => boolean;

const EXPLICIT_ORIGIN_STRENGTH = Infinity;

function matchStrength(child: NavChild, pathname: string, fromParam: string | null): number {
  if (fromParam === child.href) return EXPLICIT_ORIGIN_STRENGTH;

  const patterns = [child.href, ...(child.activeWhen ?? [])];
  return patterns.reduce((strongest, pattern) => {
    const matches = pathname === pattern || pathname.startsWith(`${pattern}/`);
    return matches ? Math.max(strongest, pattern.length) : strongest;
  }, 0);
}

export interface ActiveNavTrail {
  group: NavGroup;
  child: NavChild;
}

function resolveActiveNavTrail(pathname: string, fromParam: string | null): ActiveNavTrail | null {
  let activeTrail: ActiveNavTrail | null = null;
  let strongest = 0;

  for (const section of NAV_SECTIONS) {
    for (const group of section.groups) {
      for (const child of group.children) {
        const strength = matchStrength(child, pathname, fromParam);
        if (strength > strongest) {
          strongest = strength;
          activeTrail = { group, child };
        }
      }
    }
  }

  return activeTrail;
}

export function useActiveNavTrail(): ActiveNavTrail | null {
  const { pathname, searchStr } = useLocation();
  const fromParam = searchStr ? new URLSearchParams(searchStr).get('from') : null;
  return resolveActiveNavTrail(pathname, fromParam);
}

export function useNavGroupActive(group: NavGroup) {
  const activeHref = useActiveNavTrail()?.child.href ?? null;

  const isChildActive = (child: NavChild) => child.href === activeHref;
  const isActive = group.children.some(isChildActive);
  return { isChildActive, isActive };
}

function labelContains(label: string, query: string): boolean {
  return label.toLowerCase().includes(query);
}

export function useNavVisibilityContext(): NavVisibilityContext {
  const userTypeId = useAuthStore((state) => state.user?.userTypeId ?? null);
  const geoAccess = useAuthStore((state) => state.geoAccess);
  const { data } = useQuery({
    ...profileQueryOptions,
    enabled: userTypeId === USER_TYPE.GENERAL,
  });
  return { userTypeId, departmentName: data?.departmentName ?? null, geoAccess };
}

export interface NavAccess {
  can: (code: PermissionCode | undefined) => boolean;
  canPcmp: CanPcmp;
  visibility: NavVisibilityContext;
}

export function useNavAccess(): NavAccess {
  const { can } = usePermissions();
  const { can: canPcmp } = usePcmpPermissions();
  const visibility = useNavVisibilityContext();
  return { can, canPcmp, visibility };
}

function isChildReachable(child: NavChild, access: NavAccess): boolean {
  if (child.isHidden) return false;
  if (child.visibleWhen && !child.visibleWhen(access.visibility)) return false;
  if (!access.can(child.requires)) return false;
  return child.requiresPcmp ? access.canPcmp(...child.requiresPcmp) : true;
}

function removeUnreachableNavItems(sections: NavSection[], access: NavAccess): NavSection[] {
  return sections.flatMap((section) => {
    const groups = section.groups.flatMap((group) => {
      if (group.isHidden) return [];

      const children = group.children.filter((child) => isChildReachable(child, access));
      return children.length > 0 ? [{ ...group, children }] : [];
    });

    return groups.length > 0 ? [{ ...section, groups }] : [];
  });
}

export function hasReachableNavItem(sections: NavSection[], access: NavAccess): boolean {
  return removeUnreachableNavItems(sections, access).length > 0;
}

export function filterNavSections(
  sections: NavSection[],
  searchText: string,
  access: NavAccess,
  labelOf: (key: string) => string,
): NavSection[] {
  const visibleSections = removeUnreachableNavItems(sections, access);
  const query = searchText.trim().toLowerCase();
  if (query === '') return visibleSections;

  return visibleSections.flatMap((section) => {
    const groups = section.groups.flatMap((group) => {
      if (labelContains(labelOf(group.key), query)) return [group];

      const children = group.children.filter(
        (child) =>
          labelContains(labelOf(child.href), query) || labelContains(child.usedBy ?? '', query),
      );
      return children.length > 0 ? [{ ...group, children }] : [];
    });

    return groups.length > 0 ? [{ ...section, groups }] : [];
  });
}
